/**
 * "Today's Status" rollup (NAU-247 / NAU-267).
 *
 * Decision (locked): ticks / exclamations + traffic-light pills per domain,
 * with a thin progress bar at the top. Not per-domain progress circles. Live
 * category rows are clickable and open that category's manager hub; coming-soon
 * rows are inert placeholders.
 */

import { Button } from "@package/subframe/components/Button";
import { AlertTriangle, ArrowUp, Check } from "lucide-react";

import type { DomainStatus } from "@/lib/dashboard/todays-status";
import { DASHBOARD_STATUS_META } from "@/lib/dashboard/traffic-light";

import { OverviewCard } from "./_shared";
import { NaudBadgeIcon, toneFromStatus } from "./NaudIcon";

interface TodaysStatusPanelProps {
  domains: DomainStatus[];
  completed: number;
  total: number;
  loading?: boolean;
  error?: boolean;
  onGoToToday: () => void;
  /** Open a live category's hub. Not called for placeholder rows. */
  onSelectCategory?: (key: string) => void;
}

function DomainRow({
  domain,
  onSelect,
}: {
  domain: DomainStatus;
  onSelect?: () => void;
}) {
  const meta = DASHBOARD_STATUS_META[domain.status];
  const onTrack = domain.status === "ON_TRACK";
  const locked = Boolean(domain.locked);
  const clickable = Boolean(onSelect) && !domain.placeholder;

  const content = (
    <>
      <div className="flex items-center gap-2">
        <NaudBadgeIcon
          size="sm"
          tone={
            locked ? "blue" : onTrack ? "green" : toneFromStatus(domain.status)
          }
          icon={locked ? ArrowUp : onTrack ? Check : AlertTriangle}
        />
        <span
          className={`text-body font-body text-neutral-800 ${domain.placeholder ? "opacity-70" : ""}`}
        >
          {domain.label}
        </span>
      </div>
      {locked ? (
        <span className="text-caption font-caption-bold text-subtext-color">
          Upgrade
        </span>
      ) : (
        <span className={`text-caption font-caption ${meta.textClass}`}>
          {meta.label}
        </span>
      )}
    </>
  );

  if (!clickable) {
    return (
      <div
        data-testid={`todays-status-row-${domain.key}`}
        className="flex items-center justify-between gap-2"
        title={domain.placeholder ? "Coming soon" : undefined}
      >
        {content}
      </div>
    );
  }

  return (
    <div
      data-testid={`todays-status-row-${domain.key}`}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.();
        }
      }}
      className="-mx-2 flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1 hover:bg-neutral-50"
    >
      {content}
    </div>
  );
}

function PanelSkeleton() {
  return (
    <>
      <div className="mt-3 h-2 w-full animate-pulse rounded-full bg-neutral-100" />
      <div className="mt-2 h-3 w-2/5 animate-pulse rounded bg-neutral-100" />
      <div className="mt-4 flex flex-col gap-3">
        {/* Matches the real row count: five live categories + Risk Assessments. */}
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 animate-pulse rounded-full bg-neutral-100" />
              <div className="h-3 w-24 animate-pulse rounded bg-neutral-100" />
            </div>
            <div className="h-3 w-16 animate-pulse rounded bg-neutral-100" />
          </div>
        ))}
      </div>
    </>
  );
}

export function TodaysStatusPanel({
  domains,
  completed,
  total,
  loading,
  error,
  onGoToToday,
  onSelectCategory,
}: TodaysStatusPanelProps) {
  const needAttention = domains.filter((d) => d.status !== "ON_TRACK").length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <OverviewCard className="w-full p-5">
      <span className="text-heading-3 font-heading-3 text-primary-navy-800">
        Today’s status
      </span>

      {loading ? (
        <PanelSkeleton />
      ) : error ? (
        <span className="mt-4 text-body font-body text-subtext-color">
          Couldn’t load today’s status.
        </span>
      ) : (
        <>
          {/* Progress bar */}
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-200">
            <div
              className="h-full rounded-full bg-success-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="mt-2 text-caption font-caption text-subtext-color">
            {completed} of {total} completed
            {needAttention > 0 && (
              <>
                {" · "}
                <span className="text-warning-800 font-caption-bold">
                  {needAttention} need attention
                </span>
              </>
            )}
          </span>

          {/* Per-domain rows */}
          <div className="mt-4 flex flex-col gap-3">
            {domains.map((domain) => (
              <DomainRow
                key={domain.key}
                domain={domain}
                onSelect={
                  onSelectCategory
                    ? () => onSelectCategory(domain.key)
                    : undefined
                }
              />
            ))}
          </div>
        </>
      )}

      <Button className="mt-5 w-full" onClick={onGoToToday}>
        Go to today
      </Button>
    </OverviewCard>
  );
}
